/**
 * ComposeModal — Tier 1 Compose-and-Send flow.
 *
 * Opens a channel-aware compose panel pre-filled with a merged script,
 * lets the rep review/edit, then fires onSent() when the action launches.
 *
 * Palette: Mi6 Black #0B0B0F · Red #DC2626 · Light bg #FAFAF7 · slate text.
 * No per-role accents. No exclamation points. No emojis.
 *
 * Imports CADENCE_SCRIPTS from @/lib/template-library (owned by parallel subagent).
 * ComposeChannel type is defined locally and re-exported for CadenceQueue.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import {
  type MergeContext,
  mergeTemplate,
  getDefaultRepContext,
  buildProspectSalutation,
  isSeniorTitle,
  buildMailto,
  buildTel,
  buildSms,
  copyToClipboard,
} from "@/lib/mail-merge";
import { getSession, getCurrentTenant } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
// Import data array from template-library. TemplateScript is redefined
// locally to match the task spec; the stub may have a different shape.
import { CADENCE_SCRIPTS as _CADENCE_SCRIPTS, type ScriptChannel, type Persona } from "@/lib/template-library";
import TemplateLibraryBrowser from "@/components/templates/TemplateLibraryBrowser";
import { inferPersonaWithDefault, PERSONA_LABEL } from "@/lib/persona-from-title";
import { INDUSTRY_OPTIONS, DEFAULT_INDUSTRY } from "@/lib/industries";

// Local TemplateScript definition matching the task spec
interface TemplateScript {
  id: string;
  channel: string;
  persona?: string;
  title: string;
  subject?: string;
  body: string;
  tokens: string[];
  notes?: string;
}

const CADENCE_SCRIPTS = _CADENCE_SCRIPTS as unknown as TemplateScript[];

// ─── Constants ────────────────────────────────────────────────────────────────

const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ComposeChannel =
  | "email_opening"
  | "email_followup"
  | "linkedin_dm"
  | "phone_no_vm"
  | "phone_with_vm"
  | "sms"
  | "direct_mail";

export interface ComposeProspect {
  full_name: string;
  first_name?: string;
  title?: string;
  company: string;
  email?: string;
  phone?: string;
  linkedin?: string;
}

export interface ComposeModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional: pulls script body/subject from CADENCE_SCRIPTS by id */
  scriptId?: string;
  /** Override: custom body text (used when no scriptId or for ad-hoc compose) */
  customScript?: string;
  channel: ComposeChannel;
  prospect: ComposeProspect;
  /** e.g. "Step 1" — shown in the modal title */
  stepLabel?: string;
  /** Called when the user triggers the send action */
  onSent: () => void;
}

// ─── Channel metadata ─────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<ComposeChannel, string> = {
  email_opening: "Email Opening",
  email_followup: "Email Follow-up",
  linkedin_dm: "LinkedIn DM",
  phone_no_vm: "Phone — No Voicemail",
  phone_with_vm: "Phone + Voicemail",
  sms: "SMS",
  direct_mail: "Direct Mail",
};

type ChannelKind = "email" | "phone" | "linkedin" | "sms" | "direct_mail";

function channelKind(channel: ComposeChannel): ChannelKind {
  if (channel === "email_opening" || channel === "email_followup") return "email";
  if (channel === "phone_no_vm" || channel === "phone_with_vm") return "phone";
  if (channel === "linkedin_dm") return "linkedin";
  if (channel === "sms") return "sms";
  return "direct_mail";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildContext(prospect: ComposeProspect, industryOverride?: string): MergeContext {
  // Pull the live session user so each rep's signature renders with their
  // own name, role, and email (Partner Tenant for tenant 1; Mi6 Group for
  // mi6group.com staff). Falls back to the generic placeholder if the user
  // is not logged in (preview / public demo screens only).
  const sessionUser = getSession();
  const tenant = getCurrentTenant();
  const repCtx = getDefaultRepContext(
    sessionUser
      ? {
          name: sessionUser.name,
          email: sessionUser.email,
          role: sessionUser.role,
          tenantName: tenant?.name ?? null,
        }
      : null,
  );
  const parts = prospect.full_name.trim().split(" ");
  const firstName = prospect.first_name ?? parts[0] ?? "";
  const lastName = parts.slice(1).join(" ");
  // D-072 (2026-05-21): Industry is now an explicit picker on the Compose
  // modal (was silently auto-defaulted to "manufacturing" for BDR). If the
  // caller supplies industryOverride, use it verbatim; otherwise leave
  // empty so {industry} renders as [INDUSTRY] until the rep picks one.
  // This replaces the silent BDR "manufacturing" fallback so reps make a
  // conscious choice rather than ship copy with the wrong vertical.
  const industryDefault = industryOverride ?? "";
  // D-071: senior-buyer salutation. For C-suite/SVP/EVP/VP/President/etc.
  // titles we OVERRIDE {first_name} with the formal greeting form so every
  // existing script ("Hi {first_name}, ...") automatically becomes
  // salutation-aware without needing 150+ script edits:
  //   senior + known gender  → "Mr. Smith" / "Ms. Smith"
  //   senior + unknown gender → full name ("John Q. Smith")
  //   junior or empty title   → first name (unchanged behavior)
  // {greeting_name}, {formal_address}, and {salutation} are also exposed
  // for scripts that want to be explicit.
  const salutation = buildProspectSalutation({
    first_name: firstName,
    last_name: lastName,
    full_name: prospect.full_name,
    title: prospect.title ?? null,
    company: prospect.company,
    email: prospect.email,
  });
  const senior = isSeniorTitle(prospect.title);
  const renderedFirstName = senior && salutation.greeting_name
    ? salutation.greeting_name
    : firstName;
  return {
    ...repCtx,
    first_name: renderedFirstName,
    last_name: lastName,
    full_name: prospect.full_name,
    title: prospect.title ?? "",
    company: prospect.company,
    industry: industryDefault,
    ...salutation,
  };
}

/** Renders merged body with [TOKEN_NAME] placeholders highlighted in red */
function HighlightedBody({ text }: { text: string }) {
  const parts = text.split(/(\[[A-Z_]+\])/g);
  return (
    <p className="text-sm text-neutral-700 whitespace-pre-wrap font-sans leading-relaxed">
      {parts.map((part, i) =>
        /^\[[A-Z_]+\]$/.test(part) ? (
          <span key={i} style={{ color: MI6_RED, fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
}

// ─── Prospect summary card ────────────────────────────────────────────────────

function ProspectSummaryCard({ prospect }: { prospect: ComposeProspect }) {
  return (
    <div className="rounded-lg p-3 border border-neutral-200 bg-neutral-50 flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
        style={{ background: MI6_BLACK }}
      >
        {prospect.full_name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm" style={{ color: MI6_BLACK }}>
          {prospect.full_name}
        </div>
        {prospect.title && (
          <div className="text-[11px] text-neutral-500">
            {prospect.title}
            {prospect.company && ` · ${prospect.company}`}
          </div>
        )}
        {!prospect.title && prospect.company && (
          <div className="text-[11px] text-neutral-500">{prospect.company}</div>
        )}
        <div className="flex gap-3 mt-1 flex-wrap">
          {prospect.email && (
            <a
              href={`mailto:${prospect.email}`}
              className="text-[10px] text-neutral-500 hover:underline"
            >
              {prospect.email}
            </a>
          )}
          {prospect.phone && (
            <a
              href={buildTel(prospect.phone)}
              className="text-[10px] text-neutral-500 hover:underline"
            >
              {prospect.phone}
            </a>
          )}
          {prospect.linkedin && (
            <a
              href={
                prospect.linkedin.startsWith("http")
                  ? prospect.linkedin
                  : `https://${prospect.linkedin}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-neutral-500 hover:underline"
            >
              LinkedIn
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Copy button with feedback ────────────────────────────────────────────────

function CopyButton({
  text,
  label = "Copy",
}: {
  text: string;
  label?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      onClick={handleCopy}
      className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all"
      style={
        copied
          ? { background: "#DCFCE7", color: "#166534", border: "1px solid #BBF7D0" }
          : { background: "#EFEFEE", color: MI6_BLACK, border: "1px solid #E5E5E7" }
      }
    >
      {copied ? "Copied" : label}
    </button>
  );
}

// ─── Unfilled token warning banner ───────────────────────────────────────────

function UnmergedTokenBanner({ tokens }: { tokens: string[] }) {
  if (tokens.length === 0) return null;
  return (
    <div
      className="text-[11px] rounded-md px-3 py-2 border"
      style={{ background: "#FFF7ED", color: "#92400E", borderColor: "#FED7AA" }}
    >
      <span className="font-bold">Unfilled tokens:</span>{" "}
      {tokens.map((t, i) => (
        <span key={t}>
          <span className="font-mono font-bold" style={{ color: MI6_RED }}>
            [{t.toUpperCase()}]
          </span>
          {i < tokens.length - 1 && ", "}
        </span>
      ))}{" "}
      — review before sending.
    </div>
  );
}

// ─── Main ComposeModal ────────────────────────────────────────────────────────

export default function ComposeModal({
  open,
  onClose,
  scriptId,
  customScript,
  channel,
  prospect,
  stepLabel,
  onSent,
}: ComposeModalProps) {
  const kind = channelKind(channel);
  const { toast } = useToast();

  // D-XXX (2026-05-21, Michael 4 PM): Persona-aware script selection.
  // Earlier behavior loaded the FIRST script for the channel, which
  // dumped HR or operations copy into CFO conversations. Now we infer
  // a persona from the contact's title and prefer scripts that match
  // BOTH the channel and the inferred persona. The rep can override
  // via the persona pill in the script header.
  const inferredPersona: Persona = useMemo(
    () => inferPersonaWithDefault(prospect.title, "operations"),
    [prospect.title]
  );
  const [selectedPersona, setSelectedPersona] = useState<Persona>(inferredPersona);

  // Keep selectedPersona in sync if the modal is reopened for a different prospect.
  useEffect(() => {
    setSelectedPersona(inferredPersona);
  }, [inferredPersona]);

  // D-072: Explicit industry picker. Defaults to the platform's canonical
  // industrial-manufacturing label so the first script render is sensible
  // for Michael's book, but the rep can override per-contact without
  // editing the body manually.
  const [selectedIndustry, setSelectedIndustry] = useState<string>(DEFAULT_INDUSTRY);

  // Resolve script from library: prefer scriptId, then channel+persona,
  // then channel-only as a last-resort fallback.
  const script: TemplateScript | null = useMemo(() => {
    if (scriptId) {
      return CADENCE_SCRIPTS.find((s) => s.id === scriptId) ?? null;
    }
    // Map ComposeChannel to the ScriptChannel enum used in the library.
    const channelForLib: string =
      channel === "phone_no_vm" || channel === "phone_with_vm"
        ? "phone_script"
        : channel;
    // Primary: channel + persona match.
    const personaMatch = CADENCE_SCRIPTS.find(
      (s) => s.channel === channelForLib && s.persona === selectedPersona
    );
    if (personaMatch) return personaMatch;
    // Fallback: any script for this channel — keeps the composer populated.
    const channelMatch = CADENCE_SCRIPTS.find((s) => s.channel === channelForLib);
    if (channelMatch) return channelMatch;
    return null;
  }, [scriptId, channel, selectedPersona]);

  // Inline demo fallbacks — guarantee subject + body render even if the
  // template library is empty or shape-mismatched. Mi6 brand voice.
  const FALLBACK_BY_CHANNEL: Record<string, { subject?: string; body: string }> = {
    email_opening: {
      subject: "Quick question on your sales process at {company}",
      body: `{first_name},

I've been studying how teams at companies like {company} run pipeline reviews — specifically the gap between what the CRM says about a deal and what the rep actually knows.

Mi6 Group built a methodology called M-PACT and an intelligence layer that sits on top of the CRM. It surfaces the questions reps haven't answered yet and tells the manager which deals are real before forecast.

Worth a 20-minute conversation to see if the pattern applies at {company}?

{rep_signature}`,
    },
    email_followup: {
      subject: "Following up on pipeline visibility at {company}",
      body: `{first_name},

Circling back on my note last week. The teams we work with at companies similar to {company} share one thing in common: their forecast number is built on rep optimism rather than evidence.

Attilius applies a structured ten-checkpoint qualification framework to every active deal and gives finance and sales leadership a defensible weighted pipeline number.

Would a 20-minute walkthrough — tailored to {company} — be worth scheduling this week?

{rep_signature}`,
    },
    linkedin_dm: {
      subject: undefined,
      body: `{first_name}, short note. We help teams at companies like {company} close the gap between CRM-reported pipeline and actual deal health using a structured qualification layer (M-PACT). Open to a 15-minute conversation to see if there's a fit?`,
    },
    phone_no_vm: {
      subject: undefined,
      body: `Opening: {first_name}, this is {rep_first_name} from Mi6 Group. I work with sales leaders at companies like {company} on forecast accuracy. Did I catch you at a bad moment?

If yes: When would be a better time for a brief conversation?

If no: Most leaders I speak with tell me their CRM shows a healthy pipeline, but they know 20 to 30 percent of it won't close. We built an intelligence layer that surfaces deal-level risk before forecast. Worth a 20-minute walkthrough this week?

Hang up if no answer — no voicemail on this step.`,
    },
    phone_with_vm: {
      subject: undefined,
      body: `Opening: {first_name}, this is {rep_first_name} from Mi6 Group...

Voicemail (if no answer):
Hi {first_name}, {rep_first_name} from Mi6 Group. Reaching out because we help teams at companies like {company} close the gap between CRM-reported pipeline and actual deal health. I'll send a short note as well — best reached at {rep_phone}. Thanks.`,
    },
    sms: {
      subject: undefined,
      body: `{first_name} — {rep_first_name} from Mi6 Group. Sent you a note re: pipeline visibility at {company}. Worth a 20-minute conversation this week?`,
    },
    direct_mail: {
      subject: undefined,
      body: `Handwritten card to {first_name} at {company}.

Suggested copy:
{first_name} — enjoyed reading about {company} recently. We work with sales leaders on forecast accuracy and pipeline integrity. If the topic resonates, a short conversation could be worthwhile. — {rep_first_name}, Mi6 Group.`,
    },
  };

  const fallback = FALLBACK_BY_CHANNEL[channel];
  const rawSubject = script?.subject ?? fallback?.subject ?? "";
  const rawBody = customScript ?? script?.body ?? fallback?.body ?? "";

  const context = useMemo(
    () => buildContext(prospect, selectedIndustry),
    [prospect, selectedIndustry]
  );

  const mergedSubject = useMemo(
    () => (rawSubject ? mergeTemplate(rawSubject, context) : ""),
    [rawSubject, context]
  );
  const mergedBody = useMemo(
    () => (rawBody ? mergeTemplate(rawBody, context) : ""),
    [rawBody, context]
  );

  // Editable state for email — pre-filled with merged content
  const [editSubject, setEditSubject] = useState(mergedSubject);
  const [editBody, setEditBody] = useState(mergedBody);

  // Template browser state — additive, does not replace auto-fill
  const [browserOpen, setBrowserOpen] = useState(false);

  // Map ComposeChannel → ScriptChannel for the browser pre-filter.
  const browserInitialChannel: ScriptChannel | undefined = useMemo(() => {
    switch (channel) {
      case "email_opening":
        return "email_opening";
      case "email_followup":
        return "email_followup";
      case "linkedin_dm":
        return "linkedin_dm";
      case "phone_no_vm":
      case "phone_with_vm":
        return "phone_script";
      default:
        return undefined;
    }
  }, [channel]);

  // Sync when script or prospect changes
  useEffect(() => {
    setEditSubject(mergedSubject);
    setEditBody(mergedBody);
  }, [mergedSubject, mergedBody]);

  // Detect remaining [TOKEN] placeholders
  const unfilledTokens = useMemo(() => {
    const combined = `${editSubject}\n${editBody}`;
    const matches = combined.match(/\[([A-Z_]+)\]/g) ?? [];
    const names = matches.map((m) => m.slice(1, -1));
    return [...new Set(names)];
  }, [editSubject, editBody]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!open) return null;

  // ── Action handlers ──────────────────────────────────────────────────────

  function handleOpenEmailClient() {
    if (!prospect.email) return;
    window.open(buildMailto(prospect.email, editSubject, editBody), "_blank");
    // Do NOT call onSent() here — the step is only marked done when the
    // user explicitly clicks "Mark Done" below (B-001 fix).
    toast({
      title: "Email opened",
      description: "Click Mark Done when sent.",
    });
  }

  function handleClickToCall() {
    if (!prospect.phone) return;
    window.open(buildTel(prospect.phone));
    onSent();
  }

  function handleOpenLinkedIn() {
    if (!prospect.linkedin) return;
    const url = prospect.linkedin.startsWith("http")
      ? prospect.linkedin
      : `https://${prospect.linkedin}`;
    window.open(url, "_blank");
    onSent();
  }

  function handleOpenSms() {
    if (!prospect.phone) return;
    window.open(buildSms(prospect.phone, editBody));
    onSent();
  }

  const channelLabel = CHANNEL_LABELS[channel] ?? channel;
  const titleLabel = stepLabel
    ? `Compose · ${stepLabel} · ${channelLabel}`
    : `Compose · ${channelLabel}`;

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/40 flex items-center justify-center p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-neutral-100 flex-shrink-0"
          style={{ borderTop: `3px solid ${MI6_RED}` }}
        >
          <div>
            <div
              className="text-[9px] uppercase tracking-[0.22em] font-bold mb-0.5"
              style={{ color: MI6_RED }}
            >
              {channelLabel}
            </div>
            <div className="text-base font-bold" style={{ color: MI6_BLACK }}>
              {titleLabel}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-600 text-2xl leading-none flex-shrink-0 ml-4"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <ProspectSummaryCard prospect={prospect} />

          {/* Persona pill — inferred from contact title; rep can override */}
          <div
            className="flex items-center gap-2 flex-wrap rounded-md px-3 py-2 text-[12px]"
            style={{ background: "#FAFAF7", border: "1px solid #E5E5E7" }}
          >
            <span
              className="text-[9px] uppercase tracking-[0.18em] font-bold"
              style={{ color: MI6_RED }}
            >
              Persona
            </span>
            {(['finance', 'operations', 'hr'] as Persona[]).map((p) => {
              const active = p === selectedPersona;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelectedPersona(p)}
                  className="px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors"
                  style={
                    active
                      ? { background: MI6_BLACK, color: "#FFFFFF", border: `1px solid ${MI6_BLACK}` }
                      : { background: "#FFFFFF", color: MI6_BLACK, border: "1px solid #D4D4D4" }
                  }
                  aria-pressed={active}
                >
                  {PERSONA_LABEL[p]}
                </button>
              );
            })}
            <span className="text-[10px] text-neutral-500 ml-1">
              {prospect.title
                ? `Inferred from title: ${prospect.title}`
                : "No title on contact — defaulting to Operations"}
            </span>
          </div>

          {/* Industry picker — D-072. Was a silent BDR default; now explicit. */}
          <div
            className="flex items-center gap-2 flex-wrap rounded-md px-3 py-2 text-[12px]"
            style={{ background: "#FAFAF7", border: "1px solid #E5E5E7" }}
          >
            <span
              className="text-[9px] uppercase tracking-[0.18em] font-bold"
              style={{ color: MI6_RED }}
            >
              Industry
            </span>
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="text-[11px] font-semibold rounded-md px-2 py-1 focus:outline-none"
              style={{
                background: "#FFFFFF",
                color: MI6_BLACK,
                border: `1px solid ${MI6_BLACK}`,
                minWidth: 220,
              }}
              aria-label="Industry for this prospect"
            >
              {INDUSTRY_OPTIONS.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-neutral-500 ml-1">
              Renders as {"{industry}"} in the script. Change here to match this contact.
            </span>
          </div>

          <UnmergedTokenBanner tokens={unfilledTokens} />

          {/* ── Email channels ──────────────────────────────────────── */}
          {kind === "email" && (
            <div className="space-y-3">
              <div>
                <label className="block text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="w-full text-sm rounded-md border border-neutral-200 px-3 py-2 focus:outline-none focus:border-neutral-400"
                  style={{ color: MI6_BLACK }}
                  placeholder="Subject line…"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500">
                    Body
                  </label>
                  <button
                    type="button"
                    onClick={() => setBrowserOpen(true)}
                    className="text-[10px] uppercase tracking-[0.12em] font-bold rounded px-2 py-1 transition-colors"
                    style={{
                      background: "#FFFFFF",
                      color: MI6_BLACK,
                      border: `1px solid ${MI6_BLACK}`,
                    }}
                  >
                    Browse templates
                  </button>
                </div>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={10}
                  className="w-full text-sm rounded-md border border-neutral-200 px-3 py-2 focus:outline-none focus:border-neutral-400 resize-y font-sans leading-relaxed"
                  style={{ color: MI6_BLACK }}
                  placeholder="Email body…"
                />
              </div>
            </div>
          )}

          {/* Browse-templates affordance for non-email channels */}
          {(kind === "phone" || kind === "linkedin" || kind === "sms") && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setBrowserOpen(true)}
                className="text-[10px] uppercase tracking-[0.12em] font-bold rounded px-2 py-1 transition-colors"
                style={{
                  background: "#FFFFFF",
                  color: MI6_BLACK,
                  border: `1px solid ${MI6_BLACK}`,
                }}
              >
                Browse templates
              </button>
            </div>
          )}

          {/* ── Phone channels ──────────────────────────────────────── */}
          {kind === "phone" && (
            <div className="space-y-3">
              {channel === "phone_with_vm" && (
                <div
                  className="text-[11px] rounded-md px-3 py-2 border"
                  style={{
                    background: "#FAFAF7",
                    color: "#0B0B0F",
                    borderColor: "#E5E5E7",
                  }}
                >
                  Leave an acknowledgment voicemail if no answer.
                </div>
              )}
              {channel === "phone_no_vm" && (
                <div
                  className="text-[11px] rounded-md px-3 py-2 border"
                  style={{
                    background: "#FAFAF7",
                    color: "#525258",
                    borderColor: "#E5E5E7",
                  }}
                >
                  Hang up if no answer — no voicemail on this step.
                </div>
              )}
              {mergedBody ? (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                    Call Script
                  </div>
                  <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                    <HighlightedBody text={mergedBody} />
                  </div>
                </div>
              ) : (
                <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-4 text-center text-neutral-500 text-sm">
                  No script loaded for this step. Use your judgment.
                </div>
              )}
              {prospect.phone && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                    Phone Number
                  </div>
                  <a
                    href={buildTel(prospect.phone)}
                    className="text-sm font-medium hover:underline"
                    style={{ color: MI6_BLACK }}
                  >
                    {prospect.phone}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── LinkedIn DM ─────────────────────────────────────────── */}
          {kind === "linkedin" && (
            <div className="space-y-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                  Message
                </div>
                <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                  {mergedBody ? (
                    <HighlightedBody text={mergedBody} />
                  ) : (
                    <p className="text-sm text-neutral-500">No script loaded for this step.</p>
                  )}
                </div>
              </div>
              {prospect.linkedin && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                    Profile
                  </div>
                  <a
                    href={
                      prospect.linkedin.startsWith("http")
                        ? prospect.linkedin
                        : `https://${prospect.linkedin}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:underline"
                    style={{ color: MI6_BLACK }}
                  >
                    {prospect.linkedin}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── SMS ─────────────────────────────────────────────────── */}
          {kind === "sms" && (
            <div className="space-y-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                  Message
                </div>
                <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                  {mergedBody ? (
                    <HighlightedBody text={mergedBody} />
                  ) : (
                    <p className="text-sm text-neutral-500">No script loaded for this step.</p>
                  )}
                </div>
              </div>
              {prospect.phone && (
                <div className="text-[9px] text-neutral-500">Sending to: {prospect.phone}</div>
              )}
            </div>
          )}

          {/* ── Direct mail ─────────────────────────────────────────── */}
          {kind === "direct_mail" && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                Message Copy
              </div>
              <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                {mergedBody ? (
                  <HighlightedBody text={mergedBody} />
                ) : (
                  <p className="text-sm text-neutral-500">No script loaded for this step.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Action bar ──────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-t border-neutral-100 px-5 py-3 flex items-center gap-2 flex-wrap">
          <div className="w-full text-[10px] uppercase tracking-wider text-neutral-400 -mb-1">
            Logged locally — server sync this week
          </div>
          {kind === "email" && (
            <>
              <button
                onClick={handleOpenEmailClient}
                disabled={!prospect.email}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50 transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_BLACK }}
              >
                Open in Email Client
              </button>
              <CopyButton
                text={[editSubject ? `Subject: ${editSubject}` : "", editBody]
                  .filter(Boolean)
                  .join("\n\n")}
                label="Copy Email"
              />
              <button
                onClick={() => {
                  onSent();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_RED }}
              >
                Mark Done
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
              >
                Cancel
              </button>
            </>
          )}

          {kind === "phone" && (
            <>
              <button
                onClick={handleClickToCall}
                disabled={!prospect.phone}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50 transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_BLACK }}
              >
                Click to Call
              </button>
              <button
                onClick={() => {
                  onSent();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white"
                style={{ background: MI6_RED }}
              >
                Mark Done
              </button>
              {mergedBody && <CopyButton text={mergedBody} label="Copy Script" />}
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
              >
                Cancel
              </button>
            </>
          )}

          {kind === "linkedin" && (
            <>
              <button
                onClick={handleOpenLinkedIn}
                disabled={!prospect.linkedin}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50 transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_BLACK }}
              >
                Open LinkedIn
              </button>
              {mergedBody && <CopyButton text={mergedBody} label="Copy Message" />}
              <button
                onClick={() => {
                  onSent();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white"
                style={{ background: MI6_RED }}
              >
                Mark Done
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
              >
                Cancel
              </button>
            </>
          )}

          {kind === "sms" && (
            <>
              <button
                onClick={handleOpenSms}
                disabled={!prospect.phone}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50 transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_BLACK }}
              >
                Open SMS
              </button>
              {mergedBody && <CopyButton text={mergedBody} label="Copy" />}
              <button
                onClick={() => {
                  onSent();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white"
                style={{ background: MI6_RED }}
              >
                Mark Done
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
              >
                Cancel
              </button>
            </>
          )}

          {kind === "direct_mail" && (
            <>
              {mergedBody && <CopyButton text={mergedBody} label="Copy Copy" />}
              <button
                onClick={() => {
                  onSent();
                  onClose();
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white"
                style={{ background: MI6_RED }}
              >
                Mark Done
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Template browser — additive, does not break auto-fill */}
      <TemplateLibraryBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        initialChannel={browserInitialChannel}
        initialPersona={selectedPersona}
        onPick={(picked) => {
          const ps = picked.subject ? mergeTemplate(picked.subject, context) : "";
          const pb = mergeTemplate(picked.body, context);
          setEditSubject(ps);
          setEditBody(pb);
        }}
        title="Pick a template"
      />
    </div>
  );
}
